import * as React from "react";
import {
    Box, Stack, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    Typography, FormControl, FormHelperText, Skeleton, InputLabel, MenuItem, Select, Checkbox, ListItemText
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { DataGrid, GridColDef, GridRowSelectionModel, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useDataGridCustomLocaleText } from "../../../app/hooks";
import { useLocalization } from "@fluent/react";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { ServerErrorMsg } from "../../../app/ServerErrorMsg";
import { DocumentTypes } from "../documentTypes";
import { DocumentHeader, loadDocumentsByType } from "../documentsApi";
import { DocumentStatusLabel } from "../DocumentStatusLabel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { StructureName } from "../../device/Structure";
import { SelectButton } from "../../../app/components/SelectButton";
import { CancelButton } from "../../../app/components/CancelButton";

type Props = {
    documentType: DocumentTypes;
    value: string | null;
    selected?: DocumentHeader | null;
    filterStructureId?: string | null;
    defaultStatusFilter?: string | string[] | "all";
    reserveHelperSpace?: boolean;
    onChange: (id: string | null, doc?: DocumentHeader | null) => void
};

export const DocumentSelectField: React.FC<Props> = ({
    documentType,
    value,
    selected,
    filterStructureId,
    defaultStatusFilter = "all",
    reserveHelperSpace = true,
    onChange,
}) => {
    const dispatch = useAppDispatch();
    const localeText = useDataGridCustomLocaleText();
    const { l10n } = useLocalization();
    const [open, setOpen] = useState(false);
    const [selection, setSelection] = useState<GridRowSelectionModel>(value ? [value] : []);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<any>(null);
    const [documents, setDocuments] = useState<DocumentHeader[]>([]);
    const normalizeStatusFilter = React.useCallback((filter: Props["defaultStatusFilter"]): string[] => {
        if (filter == null || filter === "all") {
            return [];
        }

        return Array.isArray(filter) ? filter : [filter];
    }, []);

    const [statusFilter, setStatusFilter] = useState<string[]>(normalizeStatusFilter(defaultStatusFilter));

    useEffect(() => {
        setStatusFilter(normalizeStatusFilter(defaultStatusFilter));
    }, [defaultStatusFilter, normalizeStatusFilter]);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setErr(null);

        dispatch(loadDocumentsByType(documentType))
            .then(unwrapResult)
            .then((docs: DocumentHeader[]) => {
                let result = docs ?? [];

                if (filterStructureId != null) {
                    result = result.filter(d => d.structureId === filterStructureId);
                }

                setDocuments(result);
            })
            .catch((e) => setErr(e))
            .finally(() => setLoading(false));
    }, [open, documentType, filterStructureId, dispatch]);


    useEffect(() => {
        setSelection(value ? [value] : []);
    }, [value]);

    const filteredDocuments = useMemo(() => {
        if (statusFilter.length === 0) {
            return documents;
        }

        return documents.filter(document => statusFilter.includes(document.status ?? ""));
    }, [documents, statusFilter]);

    const findById = (id?: string | null) =>
        documents.find((d) => String(d.id) === String(id));

    const displayedDoc = selected ?? findById(value);
    const chooseDocumentLabel = l10n.getString(
        "document-select-choose-document-type",
        { documentType },
        `Wybierz dokument (${documentType})`
    );
    const selectedDocumentLabel = l10n.getString(
        "document-select-selected-document-type",
        { documentType },
        `Wybrany dokument (${documentType})`
    );

    const colNumber = l10n.getString("number", null, "No");
    const colTitle = l10n.getString("title", null, "Title");
    const colStructure = l10n.getString("structure", null, "Struktura");
    const colStatus = l10n.getString("status", null, "Status");
    const colCreateDate = l10n.getString("create-date", null, "Create date");
    const colLanguage = l10n.getString("language", null, "Language");
    const allStatusesLabel = l10n.getString("all", null, "All");

    const statusOptions = useMemo(
        () => [
            { value: "N", label: l10n.getString("document-status-n", null, "Nowy") },
            { value: "D", label: l10n.getString("document-status-d", null, "Zaopiniowany") },
            { value: "I", label: l10n.getString("document-status-i", null, "Do zatwierdzenia") },
            { value: "H", label: l10n.getString("document-status-h", null, "Zawieszony") },
            { value: "F", label: l10n.getString("document-status-f", null, "Zakończony") },
            { value: "C", label: l10n.getString("document-status-c", null, "Korekta") },
            { value: "A", label: l10n.getString("document-status-a", null, "Zatwierdzony") },
            { value: "P", label: l10n.getString("document-status-p", null, "Opublikowany") },
            { value: "E", label: l10n.getString("document-status-e", null, "Zaimportowany") },
            { value: "O", label: l10n.getString("document-status-o", null, "Opiniowanie") },
            { value: "R", label: l10n.getString("document-status-r", null, "Odrzucony") },
        ],
        [allStatusesLabel, l10n]
    );

    const selectedStatusLabels = useMemo(() => {
        if (statusFilter.length === 0) {
            return allStatusesLabel;
        }

        const selected = statusOptions
            .filter(option => statusFilter.includes(option.value))
            .map(option => option.label);

        return selected.length > 0 ? selected.join(", ") : allStatusesLabel;
    }, [allStatusesLabel, statusFilter, statusOptions]);

    const toolbar = React.useCallback(() => (
        <Box
            sx={{
                p: 0.5,
                pb: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
            }}
        >
            <GridToolbarQuickFilter />
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="document-select-status-filter-label">{colStatus}</InputLabel>
                    <Select
                        labelId="document-select-status-filter-label"
                        multiple
                        value={statusFilter}
                        label={colStatus}
                        size="small"
                        renderValue={() => selectedStatusLabels}
                        onChange={(event) => {
                            const value = event.target.value as string[];
                            setStatusFilter(value);
                        }}
                    >
                        {statusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                <Checkbox checked={statusFilter.includes(option.value)} size="small" />
                                <ListItemText primary={option.label} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <IconButton
                    size="small"
                    onClick={() => setStatusFilter([])}
                    disabled={statusFilter.length === 0}
                    aria-label="Wyczyść filtr statusu"
                >
                    <ClearIcon fontSize="small" />
                </IconButton>
            </Stack>
        </Box>
    ), [colStatus, statusFilter, statusOptions]);

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: "no",
                headerName: colNumber,
                width: 130,
            },
            {
                field: "title",
                flex: 1,
                headerName: colTitle,
                width: 120,
            },
            {
                field: "structureId",
                headerName: colStructure,
                flex: 1,
                minWidth: 200,
                renderCell: (params) => <StructureName structureId={params.row.structureId} />,
            },
            {
                field: "createDate",
                headerName: colCreateDate,
                width: 140,
                valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : "",
            },
            {
                field: "language",
                headerName: colLanguage,
                width: 90,
            },
            {
                field: "status", headerName: colStatus, width: 120,
                renderCell: (params) => (
                    <div>
                        <DocumentStatusLabel status={params.row.status ?? ""} />
                    </div>
                )
            }
        ],
        [colNumber, colTitle, colStructure, colStatus, colCreateDate, colLanguage]
    );

    const confirm = () => {
        const doc = selection.length
            ? documents.find(d => String(d.id) === String(selection[0])) ?? null
            : null;

        onChange(doc ? String(doc.id) : null, doc);
        setOpen(false);
    };

    const clearSelection = () => {
        setSelection([]);
        onChange(null, null);
    };

    const handleDialogClose = (_e: object, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "backdropClick") return;
        setOpen(false);
    };

    if (loading) {
        return <>
            <LoadingContainer />
        </>
    }

    return (
        <>
            <FormControl fullWidth>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                    {value ? (
                        <CheckCircleIcon sx={{ color: "success.main", fontSize: 18 }} aria-hidden />
                    ) : (
                        <PriorityHighIcon sx={{ color: "error.main", fontSize: 18 }} aria-hidden />
                    )}

                    <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: value ? "text.secondary" : "error.main" }}
                    >
                        {value ? selectedDocumentLabel : chooseDocumentLabel}
                    </Typography>
                </Stack>
                <Paper
                    variant="outlined"
                    onClick={() => setOpen(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); }
                    }}
                    sx={(theme) => ({
                        p: 1.25,
                        borderRadius: 1.5,
                        cursor: "pointer",
                        borderColor: theme.palette.divider,
                        "&:hover": { borderColor: theme.palette.text.primary },
                    })}
                >

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">

                        <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                                {colNumber}: {displayedDoc?.no ?? "—"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {colTitle}: {displayedDoc?.title ?? "—"}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            {value && (
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); clearSelection(); }} aria-label="Wyczyść">
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            )}
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(true); }} aria-label="Wybierz">
                                <SearchIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Paper>

                {reserveHelperSpace && <FormHelperText sx={{ mt: 0.75 }}>{" "}</FormHelperText>}
            </FormControl>

            <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
                <DialogTitle
                    sx={{
                        bgcolor: (theme) => theme.palette.grey[200],
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        py: 0.75,
                    }}
                >{chooseDocumentLabel}</DialogTitle>
                <DialogContent dividers>
                    {err ? (
                        <ServerErrorMsg err={err} />
                    ) : loading ? (
                        <Skeleton variant="rectangular" height="60vh" />
                    ) : (
                        <Box sx={{ height: '60vh', minHeight: 300, width: '100%' }}>
                            <DataGrid
                                rows={filteredDocuments}
                                localeText={localeText}
                                slots={{ toolbar: toolbar }}
                                getRowId={(r) => r.id}
                                columns={columns}
                                density="compact"
                                rowSelectionModel={selection}
                                onRowSelectionModelChange={(model) =>
                                    setSelection(model.length ? [String(model[0])] : [])
                                }
                                initialState={{ sorting: { sortModel: [{ field: "no", sort: "asc" }] } }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <SelectButton onClick={confirm} disabled={!selection.length} />
                    <CancelButton loading={false} onClick={() => setOpen(false)} />
                </DialogActions>
            </Dialog>
        </>
    );
};
